/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type FixedFooterMenuProps = {
  shopUid?: string;
  workspaceUid?: string;
  primaryColor?: string;
};

type ActivePage = 'mypage' | 'shop' | null;

export const FooterMenu: React.FC<FixedFooterMenuProps> = ({
  shopUid,
  workspaceUid,
  primaryColor,
}) => {
  const location = useLocation();
  const [activePage, setActivePage] = useState<ActivePage>(null);

  // 現在のパスから活性ページを判定
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/mypage')) {
      setActivePage('mypage');
    } else if (path.includes('/shops/')) {
      setActivePage('shop');
    }
  }, [location.pathname]);

  // URLを構築する共通関数
  const buildUrl = (path: string | null) => {
    if (!path || path === '#') return '#';

    // pathに既にクエリパラメータが含まれているかチェック
    const [basePath, existingQuery] = path.split('?');

    // 新しいURLSearchParamsを作成
    const newSearchParams = new URLSearchParams(existingQuery || '');

    // 現在のURLから必要なパラメータを取得して設定
    const currentSearchParams = new URLSearchParams(location.search);

    // _src パラメータを保持
    const src = currentSearchParams.get('_src');
    if (src) {
      newSearchParams.set('_src', src);
    }

    // _lineSessionKey パラメータを保持
    const lineSessionKey = currentSearchParams.get('_lineSessionKey');
    if (lineSessionKey) {
      newSearchParams.set('_lineSessionKey', lineSessionKey);
    }

    // returnPath パラメータを保持（マイページの場合）
    if (basePath.includes('/mypage')) {
      const returnPath = currentSearchParams.get('returnPath');
      if (returnPath && !newSearchParams.has('returnPath')) {
        newSearchParams.set('returnPath', returnPath);
      }
    }

    const queryString = newSearchParams.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  // マイページのパスを生成
  const getMypagePath = () => {
    if (!workspaceUid) return null;

    const currentSearchParams = new URLSearchParams(location.search);
    const existingReturnPath = currentSearchParams.get('returnPath');

    // 現在が店舗ページの場合
    if (location.pathname.includes('/shops/')) {
      // 既存のreturnPathがある場合はそれを優先（予約変更画面から来た場合など）
      if (existingReturnPath) {
        const params = new URLSearchParams();
        params.set('returnPath', existingReturnPath);
        return `/a/${workspaceUid}/mypage?${params.toString()}`;
      }

      // returnPathがない場合は、現在の店舗を設定
      if (shopUid) {
        const currentPath = `/a/${workspaceUid}/shops/${shopUid}`;
        const params = new URLSearchParams();
        params.set('returnPath', currentPath);
        return `/a/${workspaceUid}/mypage?${params.toString()}`;
      }
    }

    return `/a/${workspaceUid}/mypage`;
  };

  // 店舗トップページのパスを生成
  const getShopTopPath = () => {
    if (!workspaceUid) return null;

    // まず、URLパラメータからreturnPathを確認
    const searchParams = new URLSearchParams(location.search);
    const returnPath = searchParams.get('returnPath');

    if (returnPath) {
      // returnPathがある場合は、それを返す（既にデコードされている）
      return returnPath;
    }

    // returnPathがない場合
    if (shopUid) {
      // propsにshopUidがある場合（店舗ページから呼ばれた場合）
      return `/a/${workspaceUid}/shops/${shopUid}`;
    }

    // shopUidもない場合は店舗トップボタンを表示しない
    return null;
  };

  const shopTopPath = getShopTopPath();
  const mypagePath = getMypagePath();

  const menuItems = [
    shopTopPath && {
      icon: <HomeOutlinedIcon />,
      label: '店舗トップ',
      page: 'shop' as const,
      href: shopTopPath,
    },
    mypagePath && {
      icon: <PersonOutlineIcon />,
      label: 'マイページ',
      page: 'mypage' as const,
      href: mypagePath,
    },
  ].filter(Boolean) as Array<{
    icon: React.ReactNode;
    label: string;
    page: 'shop' | 'mypage';
    href: string;
  }>;

  return (
    <div css={styles.footer}>
      {menuItems.map((item, index) => {
        const finalUrl = buildUrl(item.href || '#');
        return (
          <a
            key={index}
            href={finalUrl}
            css={styles.menuItem}
            aria-label={item.label}
          >
            <div
              css={styles.icon(
                item.page === activePage && primaryColor
                  ? primaryColor
                  : '#b5b5b5'
              )}
            >
              {item.icon}
            </div>
            <div
              css={styles.label(
                item.page === activePage && primaryColor
                  ? primaryColor
                  : '#b5b5b5'
              )}
            >
              {item.label}
            </div>
          </a>
        );
      })}
    </div>
  );
};

const styles = {
  footer: css`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: white;
    display: flex;
    justify-content: space-around;
    align-items: center;
    height: 60px;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1000;
  `,
  menuItem: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    flex: 1;
    height: 100%;
  `,
  icon: (primaryColor: string) => css`
    color: ${primaryColor};
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      font-size: 28px;
    }
  `,
  label: (primaryColor: string) => css`
    font-size: 9px;
    line-height: 1.4;
    font-weight: 600;
    color: ${primaryColor};
    white-space: nowrap;
  `,
};
